import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import styled, { ThemeProvider as StyledThemeProvider } from "styled-components";
import { Outlet, useLocation, useNavigate } from "react-router-dom";



import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";

import createTheme from "../theme";
import { THEMES } from "../constants";
import {
  userNavsItems,
  adminNavsItems,
  sysAdminNavsItems,
  epamNavsItems,
  serverPamNavsItems,
  epamUserNavsItems,
} from "../components/sidebar/dashboardItems";

import useUserStore from "../services/userStore";
import { NGROK } from "../APIs";
import useNotificationsStore from "../services/notificationStore";
import IdleTimer from "../components/IdleTimer/IdleTimer";
import { myLocalStorage } from "../components/StorageHelper";
import useNotificationStore from "../services/notificationStore";
import { TooltipProvider } from "../components/ui/tooltip";
import { ThemeToggle } from "../components/v2components/ThemeToggle";
import { ThemeProvider as AppThemeProvider } from "../components/lib/theme-provider";
import { Toaster } from "../components/ui/toaster";
import { Power } from "lucide-react";
import useAuth from "../hooks/useAuth";
import { getClientId, logOut } from "../services/Auth";
import { clearLocalStorageOnLogout } from "../services/Helpers";
import { useKeycloakStore } from "../services/UserService";
import CollapsibleSidebar from "./CollapsibleSidebar";
import ConfirmationDialog from "../components/v2components/Endpoints/ConfirmationDialog";
import NotificationsDropdown from "../components/v2components/Notifications/NotificationsDropdown";
import "./Dashboard.css";
import { Button } from "../components/ui/button";


const Root = styled.div`
  display: flex;
  min-height: 100vh;
`;

const Dashboard = ({ children }) => {
  const [userAccessMenu, setUserAccessMenu] = useState(null);
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const headerRef = useRef(null);
  const contentRef = useRef(null);

  const location = useLocation();

  const updateHeaderOpacity = useCallback((scrollTop) => {
    const progress = Math.min(Math.max(scrollTop, 0) / 240, 1);
    headerRef.current?.style.setProperty("--header-opacity", String(1 - progress * 0.75));
  }, []);

  const handleContentScroll = useCallback((event) => {
    let element = event.target;
    let scrollTop = 0;
    while (element && contentRef.current?.contains(element)) {
      scrollTop += element.scrollTop;
      if (element === contentRef.current) break;
      element = element.parentElement;
    }
    updateHeaderOpacity(scrollTop);
  }, [updateHeaderOpacity]);

  useEffect(() => {
    updateHeaderOpacity(contentRef.current?.scrollTop ?? 0);
  }, [location.pathname, updateHeaderOpacity]);

  const userData = useUserStore((state) => state.user);
  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
  let userAccess = userData.role || myLocalStorage.getItem("role");
  const isTenantUser = userAccess === "TENANT_USER";
  let userRole = userData.userRole || "";

  const {
    setUserAWSApprovedNotifications,
    setUserAzureApprovedNotifications,
    setUserK8ApprovedNotifications,
    setUserApprovedNotifications,
    setUserGCPApprovedNotifications,
    setUserWebUrlApprovedNotifications,
    setTotalPendingCount,
  } = useNotificationStore();

  const { local_kc } = useKeycloakStore();
  const { signOut } = useAuth();
  const email = userData.email || myLocalStorage.getItem("email");
  const logoutUri = myLocalStorage.getItem("logoutUri");
  const access_token = myLocalStorage.getItem("access_token");
  const refresh_token = myLocalStorage.getItem("refresh_token");
  const [isFetching, setIsFetching] = useState(false);

  const navigate = useNavigate();

  const logOutAsInitialAdmin = () => {
    const realm = myLocalStorage.getItem("realm");
    signOut({
      realm,
      client_id: getClientId(realm),
      access_token,
      refresh_token,
    });

    navigate("/");
  };

  const logoutAsUser = () => {
    window.open(logoutUri, "_self");
    myLocalStorage.removeItem("logoutUri");
  };

  const logOutOnClientAndServer = () => {
    const isUserLoginedInitialAdmin = !logoutUri;

    try {
      if (isUserLoginedInitialAdmin) {
        logOutAsInitialAdmin();
      } else {
        logoutAsUser();
      }
    } catch (error) {
      local_kc && local_kc.doLogout();
      console.error(error);
    } finally {
      clearLocalStorageOnLogout();
    }
  };

  const openLogoutConfirmation = () => {
    setIsLogoutConfirmOpen(true);
  };

  const handleLogoutDialogChange = (open) => {
    if (!open && isLoggingOut) return;
    setIsLogoutConfirmOpen(open);
  };

  const handleSignOut = async () => {
    setIsLoggingOut(true);
    try {
      await webURlCallToSave();
      await logOut(email);

      await logOutOnClientAndServer();
    } catch (error) {
      await webURlCallToSave();
      logOutOnClientAndServer();
      console.error(error);
    } finally {
      setIsLoggingOut(false);
      setIsLogoutConfirmOpen(false);
    }
  };
  const webURlCallToSave = async () => {
    try {
      await axios.post("http://localhost:5051/webvideo-save-on-logout");
    } catch (error) {
      console.error("Error in webURlCallToSave:", error);
    }
  };

  const checkUserExists = useCallback(async () => {
    if (!latestTenant) return;

    try {
      const response = await axios.get(`${NGROK}/api/find-by-email`, {
        params: {
          email: userData.email,
        },
      });
      if (response.data === false || response.data === null) {
        console.warn("User does not exist in the current tenant.");
        logOut(userData.email)
          .then(() => {
            clearLocalStorageOnLogout();
            navigate("/signIn");
          })
          .catch((error) => {
            console.error("An error occurred during logout:", error);
          });
      }
    } catch (error) {
      console.error("Error checking user existence:", error);
    }
  }, [userData.email, latestTenant, navigate]);


  const fetchSummary = useCallback(async () => {
    if (isFetching) return; // ✅ Prevent re-entry
    setIsFetching(true);

    const controller = new AbortController();

    try {
      const email = userData.email;
      const params = {
        role: userAccess,
        tenantName: latestTenant,
      };

      if (userAccess === "TENANT_USER") {
        params.email = email;
      }

      const response = await axios.get(`${NGROK}/api/access/summary`, {
        params,
        signal: controller.signal,
      });

      if (response.data) {
        let {
          totalPendingCount,
          onPremAccess,
          awsDataAccesses,
          urlCredentialAccesses,
          azureAccess,
          k8Access,
          gcpDataAccesses,
        } = response.data;

        setTotalPendingCount(totalPendingCount);
        setUserAWSApprovedNotifications(awsDataAccesses);
        setUserAzureApprovedNotifications(azureAccess);
        setUserK8ApprovedNotifications(k8Access);
        setUserApprovedNotifications(onPremAccess);
        setUserGCPApprovedNotifications(gcpDataAccesses);
        setUserWebUrlApprovedNotifications(urlCredentialAccesses);
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        console.warn("Fetch summary request canceled");
      } else {
        console.error("Error fetching summary:", error);
      }
    } finally {
      setIsFetching(false);
    }
  }, [
    userData.email,
    userAccess,
    latestTenant
  ]);

  const fetchConfig = useCallback(async () => {
    try {
      const [admin, user] = await Promise.all([
        axios.get(`${NGROK}/api/get-all-cloudConfig?tenantName=${latestTenant}`),
        axios.get(`${NGROK}/api/get-all-usercloudConfig?tenantName=${latestTenant}&email=${userData.email}`),
      ]);

      const configData = {
        awsConfig: admin.data.awsConfig,
        azureConfig: admin.data.azureConfig,
        gcpConfig: admin.data.gcpConfig,
        awsUserConfig: user.data.awsUserConfig,
        azureUserConfig: user.data.azureUserConfig,
        gcpUserConfig: user.data.gcpUserConfig,
      };

      const setConfigs = useNotificationsStore.getState().setCloudConfigs;
      setConfigs(configData);
    } catch {
      return {};
    }
  }, [latestTenant, userData.email]);



  useEffect(() => {
    if (userRole === "epam-user") {
      setUserAccessMenu(epamUserNavsItems);
    } else if (userRole === "epam-admin") {
      setUserAccessMenu(epamNavsItems);
    } else if (userRole === "server-pam-admin") {
      setUserAccessMenu(serverPamNavsItems);
    } else if (userAccess === "TENANT_ADMIN") {
      setUserAccessMenu(adminNavsItems);
    } else if (userAccess === "SYS_ADMIN") {
      setUserAccessMenu(sysAdminNavsItems);
    } else if (userAccess === "TENANT_USER") {
      setUserAccessMenu(userNavsItems);
    }
  }, [userAccess, userRole]);

  useEffect(() => {
    if (!userData?.email || !latestTenant) return;

    fetchConfig();
    fetchSummary();
    checkUserExists();

    const summaryInterval = setInterval(() => {
      fetchSummary();
      checkUserExists();
    }, 5000);

    const configInterval = setInterval(() => {
      fetchConfig();
    }, 60000);

    return () => {
      clearInterval(summaryInterval);
      clearInterval(configInterval);
    };
  }, [fetchConfig, fetchSummary, checkUserExists, userData.email, latestTenant]);



  useEffect(() => {
    if (location.pathname === "/dashboard/cloud-analytics" || location.pathname === "/cloud/cloud-analytics") {
      window.open("/cloud-analytics-view", "_blank");
      navigate(-1);
    }
  }, [location.pathname]);

  return (
    <MuiThemeProvider theme={createTheme(THEMES.DEFAULT)}>
      <StyledThemeProvider theme={createTheme(THEMES.DEFAULT)}>
        <AppThemeProvider defaultTheme="system" storageKey="device-management-theme" persistTheme={false}>
          <Toaster />
          <Root>
            <IdleTimer />
            <TooltipProvider>
              <div className="flex w-full h-screen">
                <CollapsibleSidebar menuList={userAccessMenu} />
                <div className="relative flex min-w-0 flex-col flex-1">
                  <header
                    ref={headerRef}
                    className="dashboard-top-card mx-6 h-[52px] shrink-0 items-center rounded-b-2xl px-4 max-sm:mx-4"
                    data-plain={isTenantUser}
                  >
                    {!isTenantUser && <NotificationsDropdown />}
                    <div className="pointer-events-auto relative z-[2] col-start-3 flex items-center justify-self-end gap-2">
                      <ThemeToggle />
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="bg-red-500 text-white shadow-[0_2px_8px_rgb(15_23_42_/_0.14),0_4px_12px_-2px_rgb(239_68_68_/_0.45)] transition-colors hover:bg-red-600"
                        onClick={openLogoutConfirmation}
                        title="Logout"
                        aria-label="Logout"
                      >
                        <Power aria-hidden="true" />
                      </Button>
                    </div>
                  </header>
                  <ConfirmationDialog
                    open={isLogoutConfirmOpen}
                    onOpenChange={handleLogoutDialogChange}
                    title="Confirm Sign Out"
                    description="Are you sure you want to sign out?"
                    confirmText="Sign out"
                    cancelText="Cancel"
                    variant="destructive"
                    loading={isLoggingOut}
                    onConfirm={handleSignOut}
                  />
                  <main ref={contentRef} onScrollCapture={handleContentScroll} className="dashboard-content relative isolate z-0 min-w-0 flex-1 overflow-auto scrollbar-thin">
                    {children || <Outlet />}
                  </main>
                </div>
              </div>
            </TooltipProvider>
          </Root>
        </AppThemeProvider>
      </StyledThemeProvider>
    </MuiThemeProvider>
  );
};

export default Dashboard;
